import {
  siehtNiemandAn,
  DEFAULT_WIP_LIMIT,
  bewegung,
  countsTowardsWip,
  erledigteJeTag,
  gehoertMirImplizit,
  gruppeVon,
  imVorwarnfenster,
  isOpenStatus,
  klopftAn,
  markeVon,
  nachGruppe,
  naechsteWiedervorlage,
  nurVorraete,
  pinReihenfolge,
  pinzielSchluessel,
  pinzustand,
  poolSchlaegtVor,
  vorlaufBietetAn,
  poolVergleicher,
  serie,
  zumNehmenBereit,
  tagesziel,
  tagesschluessel,
  verlaufsTage,
  vorwarnHinweis,
  fristMarke,
  wartetAufAntwort,
  wipBereichKurz,
  wipHerkunft,
  wipHinweis,
  meilensteinDerAufgabe,
  wirksamerTermin,
  wiedervorlageFaellig,
  wirksamerMarkierTag,
  zeitfensterStand,
  zuvielGenommen,
  type Bewegungszustand,
  type ErledigterTag,
  type Gruppe,
  type Klopfmeldung,
  type Marke,
  type Fristmarke,
  type Pinziel,
  type PlanungsVorhaben,
  type Termin,
  type TaskStatus,
  type Wartezeile,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAbwesenheiten } from '../Abwesenheit'
import { imNachlauf, useNachlaufTakt } from '../../lib/gelesen'
import { useMarkierungen } from '../../lib/markierung'
import {
  aktivitaetsfeedQuery,
  aufgabenQuery,
  meilensteineQuery,
  meineVorwarnungQuery,
  personenQuery,
  poolsAllerWeltenQuery,
  schirmQuery,
  sitzungQuery,
  vorhabenAllerWeltenQuery,
  wartendeAbnahmenQuery,
  workspacesQuery,
  type Aufgabe,
  type Pool,
} from '../../lib/queries'
import { aktiverWorkspaceIst } from '../../lib/trpc'

/**
 * **Ein Ort, an dem „Mein Tag" gerechnet wird** ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * Sechs Zonen greifen auf dieselben vier Abfragen zu. Würde jede sich ihren Teil selbst
 * zusammensuchen, gäbe es sechs Auslegungen von „meine Aufgaben" – und die erste, die
 * auseinanderläuft, merkt niemand, weil beide Zahlen plausibel aussehen.
 *
 * Hier steht deshalb die **Lage**: einmal gerechnet, an die Zonen verteilt. Die Zonen sind
 * danach reine Darstellung und lassen sich mit erfundenen Daten prüfen.
 *
 * Die eigentliche Rechnerei liegt eine Ebene tiefer in `@netcon-work/shared` – hier wird nur
 * eingesammelt und verbunden.
 */

export type VorhabenSpur = {
  id: string
  label: string
  /** Wie weit es ist. Kommt fertig vom Server (`vorhaben.alle`). */
  stand: { gesamt: number; erledigt: number; offen: number }
  prozent: number
  /** Bewegt es sich? Aus dem jüngsten Ereignis über **alle** Aufgaben, nicht nur die eigenen. */
  zustand: Bewegungszustand
  bewegtSeitTagen: number | null
  /** Wer daran arbeitet – für die Kürzel auf der Karte. */
  mitwirkende: string[]
  /** Meine offenen Aufgaben darin, in der Reihenfolge, in der sie dran wären. */
  meine: Aufgabe[]
  /** Außerhalb seines Zeitfensters oder stillgelegt: steht gestrichelt da, nicht laut. */
  pausiert: boolean
  wiederAb: string | null
}

/**
 * Eine Aufgabe im Vorrat, wie das Ziehen-Fenster sie braucht.
 *
 * `pools.all` führt **weder `groesse` noch `assigneeId`** – beides steht in `tasks.all`. Statt
 * die Pool-Abfrage zu erweitern (und damit zehn Unterabfragen mehr über jede Zeile zu legen),
 * werden die zwei Felder hier aus dem Bestand dazugeholt, den der Client ohnehin hält
 * (Leitplanke 2). Ein Verbund über eine `Map` kostet nichts; eine zweite Serverabfrage schon.
 */
export type Ziehbar = Pool['aufgaben'][number] & {
  pool: Pool
  groesse: string | null
  assigneeId: string | null
  vorhabenId: string | null
  meilensteinId: string | null
  externalKey: string | null
  externalUrl: string | null
  /** Wer sie laut Work oder laut Quelle hat – für die Zeile „liegt schon bei …". */
  bearbeiterName: string | null
  /**
   * **Die Arbeitswelt** (Z-07.8) – kommt wie `groesse` aus dem Bestand, nicht aus `pools.all`.
   *
   * Ein Pool gehört zu genau einer Welt, also wäre `pool.workspaceId` derselbe Wert. Er steht
   * aber nicht in der Pool-Antwort, und die dafür zu erweitern hieße, zehn Unterabfragen mehr
   * über jede Zeile zu legen – für ein Feld, das im Client längst liegt.
   */
  workspaceId: string
}

/**
 * **Eine Zeile in der Rangfolge** ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Die gemeinsame Form für zwei Herkünfte: den **Vorrat** (`pools.all`) und das, was mir in
 * **Work** schon gehört (`tasks.all`). Vorher waren das zwei Listen mit zwei Sortierungen an
 * zwei Bildschirmrändern – und ausgerechnet die eigene hatte gar keine.
 *
 * `pool` ist `null`, sobald eine Aufgabe mir gehört: Nehmen und Zuweisen leeren `task_pool`,
 * die Zugehörigkeit ist dann Vergangenheit. Deshalb steht sie in der Rangfolge im eigenen
 * Block und nicht unter einem Pool-Reiter.
 */
export type Naechste = {
  id: string
  title: string
  sourceId: string
  externalKey: string | null
  /** Dieselbe Form wie im Bestand – `PrioBadge` verlangt die drei Stufen, nicht `string`. */
  priority: Aufgabe['priority']
  zeitfenster: string | null
  zeitfensterAb: string | Date | null
  /**
   * **Wie die Frist dasteht** – aus dem **wirksamen** Termin, nicht aus dem Zeitfenster
   * (31.08.2026, [E-281](../../../../../docs/ENTSCHEIDUNGEN.md#e-281)).
   *
   * `null` heißt **kein Termin**; nur dann darf an der Zeile „ohne Frist" stehen. Vorher las
   * die Zeile `zeitfenster` und schrieb „ohne Frist", sobald keins gesetzt war – auch bei einer
   * Aufgabe mit gerissener Fälligkeit. Genau das hat Nils am 31.08. gemeldet.
   */
  frist: Fristmarke | null
  /**
   * **Woher die Frist kommt** ([E-308](../../../../../docs/ENTSCHEIDUNGEN.md#e-308),
   * 02.09.2026): `Meilenstein „…"`, `Zieltermin des Vorhabens`, `aus dem Zeitfenster` – oder
   * `null`, wenn die Aufgabe eine eigene Fälligkeit trägt.
   *
   * Nils, 02.09.2026: *„Im Vorlauf soll bei allen Sachen das errechnete Fällig stehen."* Ein
   * Datum, das aus einem Vorhaben stammt, sieht an einer Aufgabenzeile aus wie ihres – bis
   * jemand fragt, wo es denn eingetragen sei. Die Herkunft steht deshalb im Tooltip der Zeile
   * und im Ziehen-Fenster ausgeschrieben.
   */
  fristHerkunft: string | null
  groesse: string | null
  vorhabenLabel: string | null
  /**
   * **Aus welcher Arbeitswelt die Zeile kommt** (Z-07.8).
   *
   * „Mein Tag" mischt sie bewusst – ein Mensch hat einen Tag
   * ([E-234](../../../../../docs/ENTSCHEIDUNGEN.md#e-234)). Genau deshalb braucht die Zeile
   * den Hinweis: sonst steht „Überweisung machen" ohne jeden Unterschied neben „IOR 2.0".
   */
  workspaceId: string
  gruppe: Gruppe
  marke: Marke
  /** Der Vorrat, aus dem sie käme – `null`, wenn sie mir schon gehört. */
  pool: Pool | null
  wunschIds: readonly string[]
  wunschNamen: string | null
  /** Wer sie laut Work oder laut Quelle hat – für die Zeile „liegt schon bei …". */
  bearbeiterName: string | null
  inPools: number
  /**
   * **Wieder vorgelegt** ([E-357](../../../../../docs/ENTSCHEIDUNGEN.md#e-357), 16.09.2026) – der
   * Tag, an dem eine wartende Aufgabe zurückkommen sollte, sobald er da ist. `null` an allen
   * anderen Zeilen (fehlt es, gilt dasselbe). Solche Zeilen stehen im Block „bei dir" ganz vorn.
   */
  wiedervorlage?: string | null
  /**
   * **Für Mein Tag markiert** ([Z-20](../../../../../docs/phasen/zusatz-20-mein-tag-markieren.md))
   * – der gespeicherte Tag, `null` ohne Markierung. Ob er heute gilt, rechnet `shared`.
   */
  markiertAm?: string | null
  /**
   * **„Das sieht gerade niemand an"** ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md)
   * AP 19.10) – ohne Zeitfenster, ohne Frist, ohne Wiedervorlage und seit zwei Wochen unberührt.
   *
   * Gerechnet wird es **hier**, nicht in der Zeile: `siehtNiemandAn` braucht `updatedAt`, und das
   * steht im Bestand, nicht in `Naechste`. Das Feld durchzureichen, nur damit die Anzeige es
   * selbst ausrechnet, hätte die Rechnung an zwei Orte gelegt.
   */
  uebersehen?: boolean
}

/**
 * **Eine Zeile auf dem Schirm** ([Z-03](../../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
 *
 * Die Beschriftung kommt **hier** dazu und nicht vom Server: Titel, Fortschritt und Anzahl
 * stehen im Client längst (`tasks.all`, `vorhaben.alle`), und dieselbe Zeichenkette zweimal zu
 * führen hieße, dass die zweite nach einer Umbenennung veraltet.
 *
 * `verwaist` heißt: das Ziel gibt es nicht mehr. Bei einem **Bereich** ist das kein Fehler,
 * sondern der vorhergesehene Fall – Bereiche haben keine Tabelle, also auch keinen
 * Fremdschlüssel, der den Pin mitnehmen könnte.
 */
export type Schirmzeile = {
  ziel: Pinziel
  /** Stabiler Schlüssel für `key` – der Zielschlüssel, nicht die Pin-Kennung. */
  schluessel: string
  titel: string
  notiz: string | null
  wiedervorlage: string | null
  angelegtAm: Date
  /** Die Nebenzeile: was dieses Ziel an Auskunft hergibt. */
  auskunft: string | null
  weckruf: boolean
  erledigt: boolean
  verwaist: boolean
}

/**
 * **Eine Unteraufgabe, in der ich markiert bin** (01.10.2026).
 *
 * Kommt so vom Server (`schirm.meins`). `angelegtAm` ist über tRPC eine Zeichenkette oder ein
 * `Date`, je nach Weg – die Zeile zeigt es nicht an, sie sortiert nur danach, und das macht
 * bereits der Server.
 */
export type Markierungszeile = {
  unteraufgabeId: string
  unteraufgabeTitel: string
  taskId: string
  aufgabeTitel: string
}

export type MeinTagLage = {
  ichId: string | null
  laedt: boolean

  /* Zone 0 */
  klopft: Klopfmeldung[]

  /* Auf dem Schirm (Z-03) */
  angepinnt: Schirmzeile[]
  warte: Wartezeile[]
  /** Wie viele Pins noch schlafen und wann der nächste aufwacht – die leise Zeile. */
  schlafend: { anzahl: number; naechste: string | null; zeilen: Schirmzeile[] }
  /**
   * **Offene Unteraufgaben, in denen ich markiert bin** (01.10.2026, Christian Hunhoff).
   *
   * Kommt fertig vom Server (`schirm.meins`) – hier ist nichts zu rechnen, und das ist Absicht:
   * Unteraufgaben stehen nicht im Client-Bestand, `tasks.all` führt sie nicht mit. Leere Liste,
   * solange die Abfrage läuft; die Zone verschwindet dann einfach.
   *
   * **Kein Bearbeiter** – siehe [E-345](../../../../../docs/ENTSCHEIDUNGEN.md#e-345): zählt nicht
   * gegen das WIP-Limit, steht nicht in „Jetzt", wird nicht gezogen.
   */
  markierungen: Markierungszeile[]

  /* Zone 1 */
  jetzt: Aufgabe[]
  wip: number
  /**
   * **Das Limit, das gerade gilt** – aus `wipHerkunft()`, derselben Funktion, mit der der Server
   * beim Starten entscheidet ([E-235](../../../../../docs/ENTSCHEIDUNGEN.md#e-235)).
   *
   * `Infinity`, wenn keines gilt: im persönlichen Workspace ohne eigenes Limit zählt nichts mit
   * ([E-238](../../../../../docs/ENTSCHEIDUNGEN.md#e-238)). Eigene Todos sind keine angefangene
   * Abteilungsarbeit, und `Infinity` sagt das in einer Zahl – jeder Vergleich `wip >= wipLimit`
   * fällt damit von selbst aus, ohne dass jede Aufrufstelle einen Sonderfall bekommt.
   */
  wipLimit: number
  /** Woher es kommt – für den Satz unter dem Zähler. */
  wipHinweisText: string | null
  /**
   * **Woran gemessen wird, in drei Wörtern** – „in deinen geteilten Welten" oder „in <Welt>".
   *
   * Steht **immer** dabei, nicht erst wenn der Fokus voll ist. Bei der Abnahme von Z-19 stand
   * „0 von 7 Plätzen" über drei sichtbar laufenden Karten; die Regel war richtig, die Zahl las
   * sich trotzdem wie ein Widerspruch.
   */
  wipBereich: string
  /**
   * **Fokus-Stand und Limit für eine bestimmte Arbeitswelt** (E-274).
   *
   * Seit der Vorrat über alle Welten geht, gilt je Zeile eine eigene Grenze – dieselbe, mit
   * der `tasks.starten` rechnet. `wip`/`wipLimit` daneben bleiben die der **aktiven** Welt
   * und tragen die Überschrift; diese Funktion beantwortet die Frage je Knopf.
   */
  fokusFuer: (workspaceId: string) => { wip: number; wipLimit: number }

  /* Zone 1 – die Behälter je Vorhaben, ungefiltert nachschlagbar */
  vorhabenInfo: Record<string, VorhabenSpur>

  /* Zone 2 */
  spuren: VorhabenSpur[]
  ohneVorhaben: Aufgabe[]

  /* Zone 3 – und das Fenster dahinter: eine Rangfolge, zwei Darstellungen (Z-06) */
  naechstes: Naechste[]
  /** Wie viele mir gehören, ohne dass ich sie angefangen habe – für den leisen Hinweis. */
  genommen: number
  /** Steht da, wenn jemand sich mehr genommen hat, als er anfängt. Sonst `null`. */
  zuvielHinweis: string | null

  /* Zone 4 */
  verlauf: ErledigterTag[]
  heuteFertig: number
  /** Das Ziel, das gilt: eigenes, wenn gesetzt – sonst der Vierwochenschnitt. */
  ziel: number
  /** Was in der Datenbank steht (`null` = aus dem Schnitt) – für den Schalter am Ring. */
  eigenesZiel: number | null
  serieTage: number

  /* Zone 5 */
  wartet: Aufgabe[]
  laengstesWarten: number | null
  fuerMichImVorrat: Ziehbar[]
  vorratGesamt: number
  bestandGesamt: number
  /**
   * **Was im Feed noch ungelesen ist** (28.08.2026, Z-11).
   *
   * Nils: *„das geht aktuell unten unter."* Die Zeile „Neu für dich" trug bis dahin **keine**
   * Zahl – zugeklappt war sie damit von einer leeren Zeile nicht zu unterscheiden.
   *
   * Gezählt werden **Aufgaben**, nicht Ereignisse: der Feed gruppiert je Aufgabe, also ist das
   * die Zahl der Zeilen, die man nach dem Aufklappen sieht. Eine Ereigniszahl wäre größer als
   * das, was danach dasteht – und eine Zahl, die nicht zur Liste passt, glaubt beim zweiten Mal
   * niemand mehr.
   */
  neuFuerDich: { aufgaben: number; erwaehnungen: number }

  /* Ziehen */
  pools: Pool[]
  ziehbar: Ziehbar[]
}

/** Wie viele Tage „Geschafft" und die Serie zurückblicken. Dieselbe Frist wie im Trichter. */
export const RUECKBLICK_TAGE = 28

export function useMeinTagLage(): MeinTagLage {
  const { data: sitzung } = useQuery(sitzungQuery)
  const { data: aufgaben = [], isLoading } = useQuery(aufgabenQuery)
  const { data: allePools = [] } = useQuery(poolsAllerWeltenQuery)
  const { data: personen = [] } = useQuery(personenQuery)
  /**
   * **Über alle Welten, wie der Aufgabenbestand** (30.09.2026,
   * [E-372](../../../../../docs/ENTSCHEIDUNGEN.md#e-372)). Eine laufende Aufgabe aus einer
   * anderen Arbeitswelt bringt ihr Vorhaben mit; stünde hier die gebundene Abfrage, bliebe sein
   * Behälter ohne Fortschritt und ohne Bewegung – eine Auskunft, die fehlt, weil der Umschalter
   * woanders steht.
   */
  const { data: vorhaben = [] } = useQuery(vorhabenAllerWeltenQuery)
  const { data: abnahmen = [] } = useQuery(wartendeAbnahmenQuery)
  const { data: feed = [] } = useQuery(aktivitaetsfeedQuery(14))
  const { data: schirm } = useQuery(schirmQuery)
  /**
   * **Die Arbeitswelten, in denen ich Mitglied bin** – für den Fokuszähler (Z-07.13).
   *
   * „Mein Tag" ist ungebunden und mischt sie bewusst; das **Limit** ist trotzdem eine Aussage
   * über genau eine Welt. Welche, entscheidet der Umschalter – dieselbe, in der eine neue
   * Aufgabe entstünde.
   */
  const { data: workspaces = [] } = useQuery(workspacesQuery)
  /**
   * **Die bestellten Vorwarnungen** ([Z-13](../../../../../docs/phasen/zusatz-13-frist-vorwarnung.md)).
   *
   * Zwei Abfragen, beide klein: die Abos einer Person sind eine Handvoll Zeilen, und die
   * Meilensteine braucht die Terminableitung (`wirksamerTermin`) – dasselbe Datum, das die
   * Zeitachse zeigt und der Server in die Mail schreibt. Sie hier neu zu erfinden hieße, zwei
   * Antworten auf „wann ist das fällig" zu haben.
   */
  const { data: vorwarnung } = useQuery(meineVorwarnungQuery)
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)
  const abwesenheiten = useAbwesenheiten()
  /** Z-20: was ich mir für welchen Tag vorgenommen habe – die heutigen rücken im Vorlauf vor. */
  const tagesmarken = useMarkierungen()

  /**
   * **Der Takt, der abgehakte Antworten aus „Klopft an" verschwinden lässt.** Ohne ihn bliebe
   * eine blasse Zeile stehen, bis irgendeine andere Abfrage die Lage neu rechnet – und die
   * Zusage „blendet sich gleich aus" hielte die Ansicht nicht. Er läuft nur, solange wirklich
   * etwas im Nachlauf steht; an einem Tag ohne Haken zeichnet er nichts neu.
   */
  const jetztMs = useNachlaufTakt(feed)

  const ichId = sitzung?.id ?? null

  return useMemo(() => {
    const jetztZeit = new Date()
    const heute = tagesschluessel(jetztZeit)

    /**
     * **Der wirksame Termin, einmal je Aufgabe** (31.08.2026,
     * [E-281](../../../../../docs/ENTSCHEIDUNGEN.md#e-281)).
     *
     * Nils, mit einem Bild dieser Ansicht: *„Wenn etwas Überfällig ist, dann müssen wir das
     * denke ich noch besser kennzeichnen (… RTM ist eigentlich 28.8. fällig gewesen, heute
     * haben wir den 31.)."*
     *
     * Die Ursache lag hier: „Mein Tag" fragte an **zwei** Stellen nur nach `zeitfenster` – in
     * der Zeile („ohne Frist") und in „Klopft an" (`zeitfensterAbgelaufen`). Eine Aufgabe mit
     * gesetzter **Fälligkeit** und ohne Zeitfenster fiel damit durch beide Netze: sie sah aus
     * wie eine ohne Termin, und überfällig wurde sie nie.
     *
     * Gerechnet wird jetzt mit `wirksamerTermin` – Fälligkeit → Meilenstein → Zieltermin →
     * Zeitfenster, dieselbe Reihenfolge wie in der Zeitachse, im Trichter und in der
     * Vorwarn-Mail ([E-147](../../../../../docs/ENTSCHEIDUNGEN.md#e-147)).
     *
     * **Einmal für alle Zonen**, nicht je Zone: sonst gäbe es wieder zwei Auslegungen von „bis
     * wann", und die erste, die auseinanderläuft, merkt niemand – beide Zahlen sehen plausibel
     * aus. Bei rund 3.000 Aufgaben ist es eine Schleife über den Bestand, einmal je Render.
     */
    const vorhabenNachId = new Map<string, PlanungsVorhaben>(
      vorhaben.map((v) => [
        v.id,
        {
          id: v.id,
          zeitfenster: v.zeitfenster,
          zeitfensterAb: v.zeitfensterAb,
          zieltermin: v.zieltermin,
        },
      ]),
    )
    const termine = new Map<string, Termin>(
      aufgaben.map((a) => [a.id, wirksamerTermin(a, vorhabenNachId, meilensteine, jetztZeit)]),
    )
    /** Der Termin einer Aufgabe – `null` heißt wirklich „keiner", nicht „kein Zeitfenster". */
    const terminVon = (taskId: string): Termin => termine.get(taskId) ?? null
    /**
     * **Dasselbe für eine Pool-Zeile, die nicht im Bestand steht** (Z-15).
     *
     * `pools.all` und `tasks.all` decken dieselben Arbeitswelten ab (E-274, E-234); im Alltag
     * findet `terminVon` also jede Pool-Zeile. Fehlt sie trotzdem – der Bestand lädt noch, oder
     * ein Test füttert nur den Vorrat –, wird der Termin aus dem gerechnet, was die Zeile selbst
     * mitbringt: Fälligkeit, wirksames Zeitfenster und dessen Anker (im Server schon nach E-108
     * zusammengeführt). Ohne Meilenstein und Zieltermin, weil die Zeile sie nicht kennt – aber
     * dieselbe Ableitung, nicht eine zweite. Sonst stünde ein „Jetzt" aus dem Vorrat mitten in
     * der Liste, nur weil der Bestand eine Sekunde später kam.
     */
    const terminDerPoolZeile = (a: {
      id: string
      dueAt: string | Date | null
      zeitfenster: string | null
      zeitfensterAb: string | Date | null
    }): Termin =>
      termine.has(a.id)
        ? terminVon(a.id)
        : wirksamerTermin(
            {
              dueAt: a.dueAt,
              zeitfenster: a.zeitfenster,
              zeitfensterAb: a.zeitfensterAb,
              vorhabenId: null,
              meilensteinId: null,
            },
            vorhabenNachId,
            meilensteine,
            jetztZeit,
          )

    /**
     * **Woher der Termin kommt – in Worten** ([E-308](../../../../../docs/ENTSCHEIDUNGEN.md#e-308),
     * 02.09.2026).
     *
     * Nils: *„Und auch hier: warum steht da überfällig?"* – Die Aufgabe hatte keine Fälligkeit
     * und ein Zeitfenster bis zum 07.09.; überfällig war sie wegen des **Meilensteins**
     * „Testlauf für Entwickler" (28.08.). Das stand nirgends.
     *
     * „5 Tage überfällig" ist die Zahl, die zum Handeln bewegt – aber nur, wenn man ihr glauben
     * kann. Ohne Herkunft ist sie eine Behauptung, der man an jeder anderen Stelle widerspricht.
     *
     * **Die Fälligkeit wird nicht genannt.** Sie steht an der Aufgabe selbst; wer sie gesetzt
     * hat, sucht nicht nach ihr.
     */
    const terminHerkunft = (
      termin: Termin,
      aufgabe: { vorhabenId: string | null; meilensteinId: string | null },
    ): string | null => {
      if (termin === null || termin.quelle === 'faellig') return null
      if (termin.quelle === 'zieltermin') return 'Zieltermin des Vorhabens'
      if (termin.quelle === 'zeitfenster') return 'aus dem Zeitfenster'

      /** Der Meilenstein, den die Aufgabe **bedient** – derselbe Griff wie in `wirksamerTermin` (E-335). */
      const bedient = meilensteinDerAufgabe(aufgabe, meilensteine)
      return bedient ? `Meilenstein „${bedient.label}"` : 'Meilenstein'
    }
    /**
     * **Zwei Fragen, die nicht dasselbe sind** ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
     *
     * | | Frage | Feld |
     * |---|---|---|
     * | `meine` | *wofür stehe ich gerade?* | `assigneeId` – der **wirksame** Bearbeiter |
     * | `gehoertMir` | *habe ich in **Work** zugegriffen?* | `workAssigneeId` |
     *
     * `meine` trägt Auslastung, „Geschafft" und die Serie: dort zählt auch, was mir GitLab
     * oder NetCon zuschreibt – sonst verlöre jemand seinen Tagesstand, weil er im Quellsystem
     * gearbeitet hat.
     *
     * `gehoertMir` trägt die **Rangfolge** und Zone 2, und dort ist die Quelle keine Antwort:
     * **44 von 69** ziehbaren Aufgaben tragen einen Bearbeiter aus der Quelle, ohne dass in
     * Work je jemand zugegriffen hätte (gemessen 24.08.2026). Sie standen deshalb gleichzeitig
     * links unter „Ohne Vorhaben" und rechts im Vorrat – dieselbe Zeile, zwei Bedeutungen, zwei
     * Knöpfe. Jetzt bleiben sie im Vorrat und lassen sich dort **nehmen**; erst das macht sie
     * zu meinen.
     */
    /**
     * **Meine eigenen persönlichen Arbeitswelten** ([E-278](../../../../../docs/ENTSCHEIDUNGEN.md#e-278)).
     *
     * Was darin liegt, gehört mir – auch ohne Zuweisung. Die Regel steht in `shared`, weil der
     * Server sie beim Starten genauso anwendet; hier wird nur die Menge der Welten gebildet,
     * gegen die verglichen wird.
     */
    const eigeneWelten = new Set(workspaces.filter((w) => gehoertMirImplizit(w)).map((w) => w.id))
    /** Eine Aufgabe in einer eigenen persönlichen Welt – ohne dass jemand sie zugewiesen hätte. */
    const ausEigenerWelt = (a: { workspaceId: string }) => eigeneWelten.has(a.workspaceId)

    const meine = aufgaben.filter((a) => a.assigneeId === ichId || ausEigenerWelt(a))
    const gehoertMir = (a: { workAssigneeId: string | null; workspaceId: string }) =>
      (ichId !== null && a.workAssigneeId === ichId) || ausEigenerWelt(a)

    /* ── Zone 1: was läuft ─────────────────────────────────────────────── */
    const inArbeit = meine.filter((a) => a.status === 'in_arbeit')
    const ich = personen.find((p) => p.id === ichId)
    const aktivId = aktiverWorkspaceIst()
    const aktiveWelt = workspaces.find((w) => w.id === aktivId) ?? workspaces[0]
    /**
     * **Persönliche Welten zählen in keine Abteilungsrechnung** (E-238) – in beide Richtungen.
     * Der Zählbereich „alle geteilten" braucht deshalb die Liste, gegen die er ausschließt.
     */
    const persoenlicheWelten = new Set(
      workspaces.filter((w) => w.art === 'persoenlich').map((w) => w.id),
    )

    /**
     * **Der Fokuszähler rechnet wie der Server** (Z-07.13).
     *
     * Vorher zählte er *alles*, was mir gehört, gegen mein globales Limit – auch die eigenen
     * Todos aus einem persönlichen Workspace. Damit stand in „Mein Tag" „3 von 3, Fokus voll",
     * während der Server das Starten anstandslos erlaubte: zwei Wahrheiten über dieselbe Regel,
     * und die sichtbare war die falsche.
     *
     * Jetzt beantwortet `wipHerkunft()` beides – Limit **und** Zählbereich – und zwar dieselbe
     * Funktion, die `tasks.starten` benutzt. Ein Unterschied wäre damit ein Fehler in einer
     * Funktion und nicht in zweien.
     */
    const herkunft = wipHerkunft({
      personWipLimit: ich?.wipLimit ?? DEFAULT_WIP_LIMIT,
      workspaceWipLimit: aktiveWelt?.wipLimit,
      workspaceArt: aktiveWelt?.art ?? 'geteilt',
    })
    const zaehltMit = (a: { workspaceId: string }) =>
      herkunft.zaehlt === 'workspace'
        ? a.workspaceId === aktiveWelt?.id
        : !persoenlicheWelten.has(a.workspaceId)
    const wip = meine.filter((a) => countsTowardsWip(a.status) && zaehltMit(a)).length
    const wipLimit = herkunft.limit ?? Infinity
    /**
     * **Woran der Zähler gemessen wird** (Nils, 02.10.2026, bei der Abnahme von Z-19). Hier
     * gerechnet und nicht in der Ansicht, damit die Überschrift und der Satz im Vorlauf
     * **dieselbe** Auskunft tragen – aus derselben `herkunft`, die auch die Zahl bestimmt.
     */
    const wipBereich = wipBereichKurz(herkunft, aktiveWelt?.label ?? null)

    /**
     * **Dasselbe für eine **genannte** Welt** ([E-274](../../../../../docs/ENTSCHEIDUNGEN.md#e-274)).
     *
     * Seit der Vorrat über alle Arbeitswelten geht, stehen in der Rangfolge Zeilen aus mehreren
     * Welten nebeneinander – und für jede gilt eine **andere** Grenze: `tasks.starten` rechnet
     * seit Z-07.13 mit dem Workspace **der Aufgabe**, nicht mit dem des Umschalters.
     *
     * Ohne diese Funktion prüfte der Client jede Zeile gegen die aktive Welt. Die Folge wäre die
     * ärgerlichste Sorte Fehler: ein eigenes ToDo, das der Server anstandslos starten würde,
     * stünde mit grauem Knopf und der Begründung „Fokus voll" da – gemessen an einem Limit, das
     * für es gar nicht gilt ([E-238](../../../../../docs/ENTSCHEIDUNGEN.md#e-238): persönliche
     * Welten zählen nicht mit).
     */
    const fokusFuer = (workspaceId: string): { wip: number; wipLimit: number } => {
      const welt = workspaces.find((w) => w.id === workspaceId)
      /* Unbekannte Welt: dann gilt, was auch sonst gilt – lieber die aktive Rechnung als keine. */
      if (!welt) return { wip, wipLimit }

      const h = wipHerkunft({
        personWipLimit: ich?.wipLimit ?? DEFAULT_WIP_LIMIT,
        workspaceWipLimit: welt.wipLimit,
        workspaceArt: welt.art,
      })
      const zaehlt = (a: { workspaceId: string }) =>
        h.zaehlt === 'workspace'
          ? a.workspaceId === workspaceId
          : !persoenlicheWelten.has(a.workspaceId)

      return {
        wip: meine.filter((a) => countsTowardsWip(a.status) && zaehlt(a)).length,
        wipLimit: h.limit ?? Infinity,
      }
    }

    /* ── Der Vorrat, in der Reihenfolge, in der gezogen würde ──────────── */
    /**
     * **Pool für Pool sortiert, dann aneinandergehängt** – und genau in dieser Reihenfolge
     * ([E-209](../../../../../docs/ENTSCHEIDUNGEN.md)). Vorher stand hier ein `sort` über die
     * bereits flache Liste, das ★-Aufgaben über die Pool-Grenze hinweg nach oben hob; damit
     * rutschte eine Sofort-Störung hinter zwei vorgemerkte Aufgaben. Ein `sort` über eine flache
     * Liste kennt die Pool-Grenze nicht mehr – deshalb wird hier je Pool sortiert.
     */
    const pools = nurVorraete(allePools)
    /**
     * **Der Vergleicher stuft nach dem wirksamen Termin ein** (Z-15, 14.09.2026) – deshalb
     * bekommt jede Zeile vor dem Sortieren ihr `termin` aus derselben Ableitung, die auch
     * „heute fällig" an die Zeile schreibt (`terminVon`). Nils: *„Fälligkeit direkt gesetzt
     * übersteigt Zeitfenster. Das macht die Reihenfolge aus."* Vorher sortierte nur die
     * gesetzte Stufe: eine Fälligkeit morgen ohne Zeitfenster stand hinter „10 Werktage".
     */
    const vergleicher = poolVergleicher(ichId, jetztZeit)
    const jeId = new Map(aufgaben.map((a) => [a.id, a]))
    /**
     * **Eine Aufgabe in zwei Pools ist eine Aufgabe** (15.09.2026, NCW „Bug mit Vorlauf",
     * Christian Hunhoff: *„Einträge werden doppelt angezeigt, nachdem «weitere anzeigen»
     * aufklappt und wieder zuklappt."*).
     *
     * `pools.all` führt eine Aufgabe in **jedem** Pool, in dem sie liegt – „Technikstatus
     * überarbeiten" lag in Projekte und Tickets und stand damit zweimal in der Rangfolge, mit
     * demselben `key`. React lässt bei doppelten Schlüsseln beim Verkürzen der Liste Reste
     * stehen: nach dem Zuklappen zeigte der Vorlauf dieselbe Zeile viermal, dreimal mit Rang 25.
     * E-306 hatte nur den Fall „gehört mir **und** liegt im Pool" abgefangen.
     *
     * Es gewinnt die erste Zeile in Pool-Reihenfolge: Sofort vor Tickets vor Projekte – der
     * Pool, der die Aufgabe am dringlichsten führt, sagt auch, aus welchem sie genommen wird.
     */
    const schonImVorrat = new Set<string>()
    const ziehbar: Ziehbar[] = pools.flatMap((pool) =>
      pool.aufgaben
        /** Kein zweiter Status-Vorfilter: was zählt, entscheidet `zumNehmenBereit` unten. */
        .map((a) => ({ ...a, termin: terminDerPoolZeile(a) }))
        .sort(vergleicher)
        .map((a) => {
          const voll = jeId.get(a.id)
          return {
            ...a,
            pool,
            groesse: voll?.groesse ?? null,
            assigneeId: voll?.assigneeId ?? null,
            vorhabenId: voll?.vorhabenId ?? null,
            meilensteinId: voll?.meilensteinId ?? null,
            externalKey: voll?.externalKey ?? null,
            externalUrl: voll?.externalUrl ?? null,
            bearbeiterName: voll?.assigneeName ?? a.assigneeName ?? null,
            /**
             * Leer, wenn die Aufgabe nicht im Bestand steht – die Marke schweigt dann. Lieber
             * kein Hinweis als ein geratener: `pools.all` führt die Welt nicht mit.
             */
            workspaceId: voll?.workspaceId ?? '',
          }
        })
        /**
         * **Nur, was tatsächlich zu haben ist** ([E-306](../../../../../docs/ENTSCHEIDUNGEN.md#e-306),
         * 02.09.2026).
         *
         * Bis hierher stand hier `sofortSichtbarFuer` und damit dieselbe Prüfung **nur für den
         * Sofort-Vorrat**. Sie gilt jetzt für jeden Vorrat und zusätzlich für den Status: was
         * läuft oder auf Abnahme wartet, ist kein Vorschlag. Die Regel und ihre Begründung
         * liegen in `zumNehmenBereit`.
         *
         * Steht **nach** dem Verbinden mit dem Bestand, weil `pools.all` den Bearbeiter nur als
         * Namen führt – die Kennung, gegen die geprüft wird, kommt aus `tasks.all`.
         *
         * > **Der Vorrat selbst wird davon nicht kleiner.** Die Pool-Ansicht hat ihre eigene
         * > Abfrage; hier wird nur entschieden, was „Mein Tag" **vorschlägt**.
         */
        .filter((a) =>
          zumNehmenBereit(
            {
              status: a.status as TaskStatus,
              bearbeiterId: a.assigneeId,
              wunschIds: a.wunschIds,
            },
            ichId,
          ),
        )
        /**
         * **Und nur, was der Pool anbietet** (16.09.2026,
         * [E-353](../../../../../docs/ENTSCHEIDUNGEN.md#e-353)). Nils: *„Ideen den Pool will
         * ich da nicht anzeigen … wenn die Aufgaben kein Zeitfenster, keine Fälligkeit haben."*
         *
         * `termin` ist die Ableitung von eben – dieselbe, die die Frist an die Zeile schreibt.
         * Deshalb steht der Filter hinter dem `map` und nicht davor: was hier „ohne Termin"
         * heißt, ist genau das, was die Zeile als „ohne Frist" zeigen würde. Die Regel selbst
         * liegt in `poolSchlaegtVor`, damit sie neben `zumNehmenBereit` lesbar bleibt.
         */
        /**
         * Der ★ hilft nur bei Fremdem (06.10.2026): an einer Aufgabe, die mir schon gehört, steht
         * er meist auf mir selbst – sie fiele sonst aus `eigeneZeilen` heraus und käme hier als
         * „Nehmen" wieder herein.
         */
        .filter((a) =>
          poolSchlaegtVor(
            pool,
            {
              hatTermin: a.termin !== null,
              wunschIds: (() => {
                const voll = jeId.get(a.id)
                return voll && gehoertMir(voll) ? [] : a.wunschIds
              })(),
            },
            ichId,
          ),
        )
        .filter((a) => {
          if (schonImVorrat.has(a.id)) return false
          schonImVorrat.add(a.id)
          return true
        }),
    )

    /* ── Zone 2: die Vorhaben-Spur ─────────────────────────────────────── */
    const offeneMeine = meine.filter(
      (a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt',
    )
    /**
     * **Was Work mir zuschreibt und noch offen ist** – die Grundlage von Zone 2 *und* des
     * `bei_dir`-Blocks der Rangfolge. Eine Liste für beide, damit die Zahl auf der Karte und
     * die Zeilen im Fenster nicht auseinanderlaufen können.
     */
    const offeneEigene = offeneMeine.filter(gehoertMir)
    /**
     * **Was auf eine fremde Abnahme wartet, ist keine Arbeit mehr.**
     *
     * Es steht in Zone 5 und **nur** dort. Stünde es zusätzlich in der Vorhaben-Spur, wäre es
     * ein nächster Schritt, an dem man nichts tun kann – und die Zone, die „was mache ich
     * jetzt" beantwortet, führte eine Zeile, deren einzige Antwort „warten" lautet.
     */
    const machbar = offeneEigene.filter((a) => a.status !== 'review')
    const alleSpuren: VorhabenSpur[] = vorhaben.map((v) => {
      const alleDarin = aufgaben.filter((a) => a.vorhabenId === v.id)
      const b = bewegung(alleDarin, jetztZeit)
      const stand = v.stand
      const fenster = zeitfensterStand(v.zeitfenster, v.zeitfensterAb, jetztZeit)
      return {
        id: v.id,
        label: v.label,
        stand,
        prozent: stand.gesamt > 0 ? Math.round((stand.erledigt / stand.gesamt) * 100) : 0,
        zustand: b.zustand,
        bewegtSeitTagen: b.tage,
        mitwirkende: [
          ...new Set(
            alleDarin
              .map((a) => a.assigneeName)
              .filter((n): n is string => Boolean(n))
              .concat(v.mitOwnerName ?? []),
          ),
        ],
        meine: machbar.filter((a) => a.vorhabenId === v.id),
        /**
         * **Pausiert ist ein Zustand, kein Verschwinden.** Ein stillgelegtes Vorhaben oder
         * eines, dessen Zeitfenster noch nicht läuft, steht gestrichelt da. Nicht weg, nicht
         * laut – man weiß, dass es existiert, ohne sich schuldig zu fühlen.
         */
        pausiert: !v.aktiv,
        wiederAb: fenster.datum,
      }
    })

    /**
     * **Dieselbe Rechnung, ungefiltert** – für die Vorhaben-Behälter in Zone 1.
     *
     * „Jetzt" zeigt seit dem 25.08.2026 links je Vorhaben einen Block mit Fortschritt und
     * Bewegung. Es darf dafür **nicht** `spuren` benutzen: die Liste ist auf „wo ich eine offene
     * eigene Aufgabe habe" gefiltert, und Zone 1 folgt dem *wirksamen* Bearbeiter. Eine Aufgabe,
     * die laut GitLab bei mir läuft, stünde dann in „Jetzt" unter einem Vorhaben, dessen
     * Fortschritt niemand kennt.
     *
     * Ein `Record` und keine Liste: Zone 1 schlägt je Aufgabe nach, sie sortiert nicht.
     */
    const vorhabenInfo: Record<string, VorhabenSpur> = Object.fromEntries(
      alleSpuren.map((s) => [s.id, s]),
    )

    const spuren: VorhabenSpur[] = alleSpuren
      /**
       * **„Meine Vorhaben" heißt: meine.** Ein Vorhaben steht hier, wenn ich darin eine offene
       * Aufgabe habe – sonst nicht. Alles Übrige steht in der Vorhaben-Ansicht; hier wäre es
       * genau die Liste, die „Mein Tag" gerade loswerden will.
       */
      .filter((s) => s.meine.length > 0)
      /**
       * Was sich bewegt, zuerst; Pausiertes zuletzt. Innerhalb dessen das Vorhaben mit den
       * meisten offenen eigenen Aufgaben – daran arbeitet man gerade.
       */
      .sort((a, b) => {
        const rang = (s: VorhabenSpur) => (s.pausiert ? 2 : s.zustand === 'steht' ? 1 : 0)
        return rang(a) - rang(b) || b.meine.length - a.meine.length
      })

    /**
     * **„Ohne Vorhaben" ist ein Fach, kein Rest.** Bei NetCon-Tickets hat die Mehrheit kein
     * Vorhaben; ohne diese Karte wäre die Hauptspur für jeden gelogen, der überwiegend Tickets
     * macht. Laufendes steht schon in Zone 1 und wird hier nicht doppelt gezeigt.
     */
    const ohneVorhaben = machbar.filter((a) => !a.vorhabenId && a.status !== 'in_arbeit')

    /* ── Die eine Rangfolge (Z-06) ─────────────────────────────────────── */
    /**
     * **Was als Nächstes dran ist – aus beiden Herkünften, nach einer Regel.**
     *
     * Nils, 24.08.2026: *„Ich denke es ist für einen Entwickler noch sehr schwierig zu
     * unterscheiden, wann er Aufgaben ziehen soll und wann er auf seinen Vorrat zugreift."*
     *
     * Die Antwort ist nicht, eine der beiden Listen zu verstecken, sondern sie zu **einer** zu
     * machen: was mir gehört und noch nicht läuft, steht neben dem, was im Vorrat liegt. Die
     * Marke an der Zeile sagt, welcher Knopf gilt – `bei_dir` → Starten, sonst Nehmen *oder*
     * Starten.
     *
     * ## Die Reihenfolge steht in `shared`
     *
     * `nachGruppe` sortiert **nur** nach Sofort → Eigenes → Vorrat; innerhalb der Gruppen
     * bleibt, was `poolVergleicher` je Pool entschieden hat (stabiler `sort`). Das Eigene wird
     * hier vorher mit demselben Vergleicher geordnet – sonst hätte ausgerechnet die eigene
     * Hälfte wieder keine Ordnung, und genau das war der Mangel.
     */
    /**
     * **Die Pool-Zugehörigkeit, umgedreht** – `tasks.all` führt sie nicht mit, nur `pools.all`
     * kennt sie. Für die Regel unten braucht es die Richtung Aufgabe → Pöple, also einmal
     * umgehängt. Über **alle** Pöple, nicht nur die Vorräte: Auch ein Parkplatz ist ein Pool,
     * und seine Einstellung soll gelten.
     */
    const poolsJeAufgabe = new Map<string, string[]>()
    for (const pool of allePools) {
      /* Ein Pool ohne Aufgabenliste ist denkbar (Testaufbauten, alte Antworten) – kein Grund zu stolpern. */
      for (const a of pool.aufgaben ?? []) {
        const vorhanden = poolsJeAufgabe.get(a.id)
        if (vorhanden) vorhanden.push(pool.id)
        else poolsJeAufgabe.set(a.id, [pool.id])
      }
    }

    const eigeneZeilen: Naechste[] = machbar
      /**
       * **Wartendes schläft bis zu seinem Tag** (E-357). Bis zum 16.09.2026 stand eine Aufgabe in
       * „Wartet auf Antwort" die ganze Zeit hier – als Vorschlag für etwas, an dem man nichts tun
       * kann. Am Tag der Wiedervorlage kommt sie über `wiedervorlageZeilen` zurück, ganz vorn.
       */
      .filter((a) => a.status !== 'in_arbeit' && a.status !== 'warten')
      /**
       * **Die Pool-Regel gilt auch für Eigenes** ([E-394](../../../../../docs/ENTSCHEIDUNGEN.md#e-394),
       * 01.10.2026).
       *
       * `poolSchlaegtVor` wurde bisher nur unten bei den Vorrat-Zeilen gefragt – also nur für
       * Aufgaben, die noch niemandem gehören. Nils fand am 01.10. vier termin- und priolose Ideen
       * im Rad und sagte: *„Da dürfen nur wirklich Sachen aus dem Vorlauf rein."* Beim Nachmessen
       * standen sie **auch hier** – weil sie ihm gehören und die Regel für Eigenes nie galt. Bei
       * jemandem, der fast alles sich selbst zuweist, griff sie damit praktisch nie.
       *
       * Der Pool bleibt unberührt: Dort stehen die Ideen weiter, und über „Alle Aufgaben" sind
       * sie erreichbar. Hier wird nur entschieden, was „Mein Tag" **vorschlägt**.
       */
      .filter((a) =>
        vorlaufBietetAn(
          {
            hatTermin: terminVon(a.id) !== null,
            /**
             * **Ohne ★ – sie gehört mir schon** (Nils, 06.10.2026: *„Die Ideen ohne Frist usw.
             * dürfen hier auch nicht auftauchen – das ist echt nur Ablage."*).
             *
             * Hier stand die Wunschliste der Aufgabe, und `poolSchlaegtVor` lässt durch, wofür ich
             * vorgemerkt bin. Das ist für **fremden** Vorrat gedacht: wer ausdrücklich in Frage
             * kommt, soll einspringen können. An einer eigenen Aufgabe steht der ★ aber fast immer
             * auf mir selbst – nachgemessen an „1 Euro Buttons", „Mindmap Blase" und „Verschiedene
             * Anbieter" im Pool Ideen. Die Ausnahme hob die Regel „nur mit Termin" damit genau
             * dort auf, wofür sie gemacht ist. Dieselbe Überlegung wie beim Leeren unten: die
             * Frage *„für wen ist sie gedacht"* ist beantwortet. Kalender und Rad reichen den ★
             * ohnehin nicht weiter.
             */
            wunschIds: [],
            poolIds: poolsJeAufgabe.get(a.id) ?? [],
          },
          allePools,
          ichId,
        ),
      )
      /**
       * **`wunschIds` wird hier geleert, bevor sortiert wird** – und das ist keine Bequemlichkeit.
       *
       * In `tasks.all` ist das Feld die rohe Zeichenkette aus `GROUP_CONCAT`, im Vorrat dagegen
       * schon ein Array. Dieselbe Zeichenkette an `poolVergleicher` zu geben ginge stillschweigend
       * schief: `''.length === 0` stimmt zufällig, `'abc'.includes(id)` vergleicht Zeichen statt
       * Kennungen. Und inhaltlich sagt ★ an einer Aufgabe, die mir schon gehört, ohnehin nichts
       * mehr – die Frage *„für wen ist sie gedacht"* ist beantwortet.
       */
      .map((a) => ({ ...a, wunschIds: [] as string[], termin: terminVon(a.id) }))
      .sort(vergleicher)
      .map((a) => ({
        id: a.id,
        title: a.title,
        sourceId: a.sourceId,
        externalKey: a.externalKey,
        priority: a.priority,
        zeitfenster: a.zeitfenster,
        zeitfensterAb: a.zeitfensterAb,
        frist: fristMarke(terminVon(a.id), jetztZeit),
        fristHerkunft: terminHerkunft(terminVon(a.id), a),
        groesse: a.groesse,
        vorhabenLabel: a.vorhabenLabel,
        workspaceId: a.workspaceId,
        gruppe: gruppeVon({ gehoertMir: true, durchbrichtWip: false }),
        marke: markeVon({ gehoertMir: true }, ichId),
        pool: null,
        wunschIds: [],
        wunschNamen: null,
        bearbeiterName: a.assigneeName,
        inPools: 0,
        wiedervorlage: null,
        uebersehen: siehtNiemandAn(a),
      }))

    const vorratZeilen: Naechste[] = ziehbar.map((a) => ({
      id: a.id,
      title: a.title,
      sourceId: a.sourceId,
      externalKey: a.externalKey,
      priority: a.priority,
      zeitfenster: a.zeitfenster,
      zeitfensterAb: a.zeitfensterAb,
      frist: fristMarke(terminVon(a.id), jetztZeit),
      fristHerkunft: terminHerkunft(terminVon(a.id), a),
      groesse: a.groesse,
      vorhabenLabel: a.vorhabenLabel,
      workspaceId: a.workspaceId,
      gruppe: gruppeVon({ gehoertMir: false, durchbrichtWip: a.pool.wipDurchbrechen }),
      marke: markeVon({ gehoertMir: false, wunschIds: a.wunschIds }, ichId),
      pool: a.pool,
      wunschIds: a.wunschIds,
      wunschNamen: a.wunschNamen,
      bearbeiterName: a.bearbeiterName,
      inPools: a.inPools,
      wiedervorlage: null,
      uebersehen: siehtNiemandAn(a),
    }))

    /**
     * **Jede Aufgabe genau einmal** ([E-306](../../../../../docs/ENTSCHEIDUNGEN.md#e-306)).
     *
     * Aus Ticket NCW-6X848M: *„Vorlauf zeigt doppelte Aufgaben an."* – Er tat es, und zwar
     * genau dort, wo beide Hälften dieselbe Aufgabe trafen: **sie gehört mir und liegt noch im
     * Pool.** Nehmen entfernt eine Aufgabe nicht aus ihrem Vorrat (das Regal bleibt vollständig),
     * also stand sie links als „gehört dir" und rechts als Vorschlag. Gemessen am 02.09.2026 bei
     * vier Personen, in der Spitze vier Zeilen.
     *
     * **Die eigene Hälfte gewinnt**, und das ist nicht beliebig: sie trägt die Marke „gehört
     * dir" und den Knopf „Starten". Die Pool-Zeile böte „Nehmen" an – für etwas, das man schon
     * hat.
     */
    const eigeneIds = new Set(eigeneZeilen.map((z) => z.id))
    /**
     * **Was die Quelle mir zuschreibt und in keinem Vorrat liegt**
     * ([E-316](../../../../../docs/ENTSCHEIDUNGEN.md#e-316), 04.09.2026).
     *
     * Aus Ticket NCW-6X848M: *„Aufgabe «How To Use NetConWeb…» fehlt in meinem Vorlauf
     * (Davin)."* – Sie fiel durch **beide** Hälften:
     *
     * | Hälfte | verlangt | die Aufgabe |
     * |---|---|---|
     * | eigene (`machbar`) | in **Work** gezogen (`work_assignee_id`) | nur in GitLab zugewiesen |
     * | Vorrat (`ziehbar`) | liegt in einem ziehbaren Pool | liegt in **keinem** |
     *
     * Beide Bedingungen sind für sich richtig – die Lücke entsteht erst dazwischen, und sie ist
     * die schlechteste Sorte: die Aufgabe ist **niemandem** sichtbar. Wer sie im Vorrat suchen
     * würde, findet sie nicht, weil sie in keinem liegt; wer sie unter „gehört dir" suchen
     * würde, findet sie nicht, weil er sie nie gezogen hat. Gemessen am 04.09.2026: **fünf**
     * Aufgaben.
     *
     * **Die Marke ist `vorgemerkt`, nicht `bei_dir`.** Sie gehört mir noch nicht – gezogen wird
     * in Work, und bis dahin steht am Knopf „Nehmen". Die Gruppe ist `vorrat`, damit sie hinter
     * dem steht, was ich wirklich schon habe.
     *
     * **Ohne Pool und trotzdem im Vorlauf** – das ist die Ausnahme, die den Rest der Regel
     * bestätigt: `zumNehmenBereit` ([E-306](../../../../../docs/ENTSCHEIDUNGEN.md#e-306)) fragt,
     * ob eine Aufgabe **im Vorrat** zu haben ist. Diese hier ist nicht im Vorrat, sondern
     * ausdrücklich für mich da.
     */
    const imVorratIds = new Set(pools.flatMap((p) => p.aufgaben.map((a) => a.id)))
    const zugeschriebenZeilen: Naechste[] = offeneMeine
      .filter(
        (a) =>
          !gehoertMir(a) &&
          a.assigneeId === ichId &&
          (a.status === 'eingang' || a.status === 'bereit') &&
          !imVorratIds.has(a.id),
      )
      /** Dieselbe Umformung wie bei `eigeneZeilen`: `wunschIds` ist hier eine rohe Zeichenkette. */
      .map((a) => ({ ...a, wunschIds: [] as string[], termin: terminVon(a.id) }))
      .sort(vergleicher)
      .map((a) => ({
        id: a.id,
        title: a.title,
        sourceId: a.sourceId,
        externalKey: a.externalKey,
        priority: a.priority,
        zeitfenster: a.zeitfenster,
        zeitfensterAb: a.zeitfensterAb,
        frist: fristMarke(terminVon(a.id), jetztZeit),
        fristHerkunft: terminHerkunft(terminVon(a.id), a),
        groesse: a.groesse,
        vorhabenLabel: a.vorhabenLabel,
        workspaceId: a.workspaceId,
        gruppe: gruppeVon({ gehoertMir: false, durchbrichtWip: false }),
        marke: 'vorgemerkt' as const,
        pool: null,
        wunschIds: [],
        wunschNamen: null,
        bearbeiterName: a.assigneeName,
        inPools: 0,
        wiedervorlage: null,
        uebersehen: siehtNiemandAn(a),
      }))

    /**
     * **Was ich mir wieder vorgelegt habe** ([E-357](../../../../../docs/ENTSCHEIDUNGEN.md#e-357),
     * 16.09.2026). Nils: *„Erinnere mich in 1, 2, 3, 4, 5 Tagen, dass ich die Aufgabe wieder im
     * Vorlauf habe."*
     *
     * Bis hierher klopfte eine fällige Wiedervorlage in Zone 0 an (E-315). Sie ist aber ein
     * Termin mit mir selbst, kein Notfall – und die Frage, die sie stellt, ist *„was mache ich
     * als Nächstes"*. Deshalb steht sie jetzt hier: im Block „bei dir" **ganz vorn**, die älteste
     * zuerst. Nur 🔥 Sofort steht noch davor (E-209) – eine Störung wartet auch nicht auf meine
     * Verabredung mit mir selbst.
     *
     * Aus `offeneMeine` und nicht aus `machbar`: auch eine Aufgabe, die mir nur die Quelle
     * zuschreibt, soll zurückkommen, wenn ich sie auf Wiedervorlage gelegt habe.
     */
    const wiedervorlageZeilen: Naechste[] = offeneMeine
      .filter((a) => wiedervorlageFaellig(a, heute))
      .sort((a, b) => (a.wartenBis ?? '').localeCompare(b.wartenBis ?? ''))
      .map((a) => ({
        id: a.id,
        title: a.title,
        sourceId: a.sourceId,
        externalKey: a.externalKey,
        priority: a.priority,
        zeitfenster: a.zeitfenster,
        zeitfensterAb: a.zeitfensterAb,
        frist: fristMarke(terminVon(a.id), jetztZeit),
        fristHerkunft: terminHerkunft(terminVon(a.id), a),
        groesse: a.groesse,
        vorhabenLabel: a.vorhabenLabel,
        workspaceId: a.workspaceId,
        gruppe: gruppeVon({ gehoertMir: true, durchbrichtWip: false }),
        marke: gehoertMir(a) ? ('bei_dir' as const) : ('vorgemerkt' as const),
        pool: null,
        wunschIds: [],
        wunschNamen: null,
        bearbeiterName: a.assigneeName,
        inPools: 0,
        wiedervorlage: a.wartenBis ?? null,
        uebersehen: siehtNiemandAn(a),
      }))
    const wiedervorlageIds = new Set(wiedervorlageZeilen.map((z) => z.id))

    const grundfolge = nachGruppe([
      ...wiedervorlageZeilen,
      ...eigeneZeilen,
      ...vorratZeilen.filter((z) => !eigeneIds.has(z.id) && !wiedervorlageIds.has(z.id)),
      ...zugeschriebenZeilen.filter((z) => !wiedervorlageIds.has(z.id)),
    ])

    /**
     * **Was ich mir für heute vorgenommen habe, rückt nach vorn**
     * ([Z-20](../../../../../docs/phasen/zusatz-20-mein-tag-markieren.md), 05.10.2026).
     *
     * Die Reihenfolge ist **Sofort → heute markiert → bei dir → Vorrat**. Sofort schlägt auch die
     * Markierung, aus demselben Grund wie den Wunsch-Entwickler
     * ([E-209](../../../../../docs/ENTSCHEIDUNGEN.md)): eine Störung wartet nicht auf meine
     * Planung. Liegengeblieben zählt für heute ([F-95](../../../../../docs/OFFENE-FRAGEN.md#f-95)).
     *
     * **Eine Markierung ist selbst ein Termin.** Der Vorlauf bietet sonst nur Aufgaben mit Termin
     * an ([E-394](../../../../../docs/ENTSCHEIDUNGEN.md#e-394)) – eine termin- und priolose Idee,
     * die ich ausdrücklich für heute markiert habe, fiele dort durch. Deshalb wird nachgeholt,
     * was markiert ist und in der Grundfolge fehlt; auch Fremdes ohne Pool, denn markieren ist
     * nicht nehmen ([F-97](../../../../../docs/OFFENE-FRAGEN.md#f-97)) – „Starten" nimmt es dann.
     */
    const heuteMarkiert = (id: string) => {
      const am = tagesmarken.get(id)
      return am !== undefined && wirksamerMarkierTag(am, heute) === heute
    }
    const inGrundfolge = new Set(grundfolge.map((z) => z.id))
    const nachgeholt: Naechste[] = aufgaben
      .filter(
        (a) =>
          !inGrundfolge.has(a.id) &&
          (a.status === 'eingang' || a.status === 'bereit') &&
          heuteMarkiert(a.id),
      )
      .map((a) => {
        const mir = gehoertMir(a)
        const pool = pools.find((p) => (p.aufgaben ?? []).some((x) => x.id === a.id)) ?? null
        return {
          id: a.id,
          title: a.title,
          sourceId: a.sourceId,
          externalKey: a.externalKey,
          priority: a.priority,
          zeitfenster: a.zeitfenster,
          zeitfensterAb: a.zeitfensterAb,
          frist: fristMarke(terminVon(a.id), jetztZeit),
          fristHerkunft: terminHerkunft(terminVon(a.id), a),
          groesse: a.groesse,
          vorhabenLabel: a.vorhabenLabel,
          workspaceId: a.workspaceId,
          gruppe: gruppeVon({ gehoertMir: mir, durchbrichtWip: pool?.wipDurchbrechen ?? false }),
          marke: mir
            ? ('bei_dir' as const)
            : a.assigneeId === ichId
              ? ('vorgemerkt' as const)
              : ('herrenlos' as const),
          pool,
          wunschIds: [],
          wunschNamen: null,
          bearbeiterName: a.assigneeName,
          inPools: pool ? 1 : 0,
          wiedervorlage: null,
          uebersehen: false,
        }
      })
    const markierRang = (z: Naechste) => (z.gruppe === 'sofort' ? 0 : heuteMarkiert(z.id) ? 1 : 2)
    /** Stabil: innerhalb jeder Stufe bleibt die Reihenfolge von `nachGruppe` und `poolVergleicher`. */
    const naechstes: Naechste[] = [...grundfolge, ...nachgeholt]
      .map((z) => ({ ...z, markiertAm: tagesmarken.get(z.id) ?? null }))
      .sort((a, b) => markierRang(a) - markierRang(b))

    /**
     * **Der leise Hinweis auf zu viel Genommenes** (Nils, 24.08.2026 – keine harte Grenze).
     *
     * Ohne WIP-Sperre am Nehmen kann sich jemand den halben Vorrat an sich ziehen, ohne dass
     * es auffällt. Die Regel und ihre Begründung stehen in `zuvielGenommen`; hier werden nur
     * die zwei Zahlen eingesammelt.
     */
    const genommen = eigeneZeilen.length

    /* ── Zone 4: geschafft ─────────────────────────────────────────────── */
    const tage = verlaufsTage(jetztZeit, RUECKBLICK_TAGE)
    const verlauf = erledigteJeTag(meine, tage)
    const heuteFertig = verlauf.at(-1)?.anzahl ?? 0
    const frei = ichId
      ? (() => {
          const weg = abwesenheiten.abwesendAn(ichId, tage)
          return (t: string) =>
            weg.has(t) || [0, 6].includes(new Date(`${t}T12:00:00Z`).getUTCDay())
        })()
      : (t: string) => [0, 6].includes(new Date(`${t}T12:00:00Z`).getUTCDay())

    /* ── Zone 5: ruhig gestellt ────────────────────────────────────────── */
    const wartet = meine.filter((a) => a.status === 'review')
    const meineAbnahmen = abnahmen.filter((a) => wartet.some((w) => w.id === a.id))
    const laengstesWarten = meineAbnahmen.length
      ? meineAbnahmen.reduce((max, a) => Math.max(max, a.wartetTage ?? 0), 0)
      : null
    const fuerMichImVorrat = ziehbar.filter((a) => ichId !== null && a.wunschIds.includes(ichId))

    /* ── Zone 0: klopft an ─────────────────────────────────────────────── */
    /**
     * Die vier Sorten stehen in `shared/meintag.ts`; hier wird nur zusammengetragen, was sie
     * brauchen. Eng gefasst, damit die Zone meistens leer bleibt – das ist ihre wichtigste
     * Eigenschaft.
     */
    const laufendeIds = new Set(inArbeit.map((a) => a.id))

    /**
     * **Die bestellten Vorwarnungen** (Z-13).
     *
     * Drei Bedingungen, und alle drei müssen stimmen: der System-Kanal ist an, es gibt ein Abo
     * (meins oder ein fremdes mit „alle Bearbeiter"), und der **wirksame** Termin liegt im
     * eigenen Vorwarnfenster. Das Datum kommt aus derselben Ableitung wie überall sonst –
     * Fälligkeit → Meilenstein → Zieltermin → Zeitfenster ([E-147](../../../../../docs/ENTSCHEIDUNGEN.md#e-147)).
     *
     * Erledigtes und Zurückgestelltes fällt heraus: eine Vorwarnung zu etwas Abgeschlossenem
     * ist der schnellste Weg, dass jemand die Erinnerungen abschaltet.
     */
    const vorwarnungen = (() => {
      const abos = vorwarnung?.aufgaben ?? []
      if (abos.length === 0 || vorwarnung?.einstellung.imSystem !== true) return []

      return abos.flatMap((abo) => {
        const a = aufgaben.find((x) => x.id === abo.taskId)
        if (!a || a.status === 'erledigt' || a.status === 'zurueckgestellt') return []
        /**
         * **Die Frist dieser Aufgabe schlägt die Voreinstellung**
         * ([E-324](../../../../../docs/ENTSCHEIDUNGEN.md#e-324), 04.09.2026) – genau wie im
         * Mail-Kanal. Zwei Kanäle, die verschieden rechnen, wären zwei Antworten auf „wann
         * höre ich davon".
         */
        const tage = abo.tage ?? vorwarnung.einstellung.tage
        /** Dieselbe Karte wie oben – zwei Rechnungen wären zwei Antworten auf „bis wann". */
        const termin = terminVon(a.id)
        if (termin === null || !imVorwarnfenster(termin.datum, tage, jetztZeit)) return []
        return [
          {
            taskId: a.id,
            titel: a.title,
            termin: termin.datum,
            hinweis: vorwarnHinweis(termin.datum, jetztZeit),
          },
        ]
      })
    })()

    const klopft = ichId
      ? klopftAn({
          ichId,
          heute,
          /**
           * **Ungezogen – und entweder herrenlos oder meine** (24.08.2026).
           *
           * Vorher stand hier nur `!assigneeId`. Seit eine vergebene Sofort-Störung aus dem
           * Vorrat der anderen verschwindet, wäre sie damit **nirgends** mehr gemeldet: die
           * Kollegen sehen sie nicht mehr, und beim Namensträger klopfte sie nie an. Genau der
           * gemessene Fall – die Quelle nennt einen Namen, in Work hat sie niemand gezogen.
           *
           * `ziehbar` ist schon gefiltert; wer hier auftaucht, darf sie also ohnehin sehen.
           */
          sofortImVorrat: ziehbar
            .filter((a) => a.pool.wipDurchbrechen && (!a.assigneeId || a.assigneeId === ichId))
            .map((a) => ({ id: a.id, title: a.title, addedAt: a.addedAt })),
          /**
           * **Die Frist kommt aus dem wirksamen Termin** (E-281). Vorher stand hier zweimal
           * `zeitfensterStand(...)` – und damit meldete eine gerissene **Fälligkeit** nichts,
           * weil sie in `due_at` steht und mit dem Zeitfenster nichts zu tun hat.
           */
          meine: offeneMeine.map((a) => {
            const termin = terminVon(a.id)
            const marke = fristMarke(termin, jetztZeit)
            const herkunft = terminHerkunft(termin, a)
            return {
              id: a.id,
              title: a.title,
              status: a.status,
              fristAbgelaufen: marke?.stufe === 'ueberfaellig',
              /** Die Zahl **und** woher sie kommt – siehe `terminHerkunft` (E-308). */
              fristText: marke ? (herkunft ? `${marke.text} · ${herkunft}` : marke.text) : null,
            }
          }),
          meineAbnahmen: aufgaben
            .filter((a) => a.abnahmeDurchId === ichId && a.status === 'review')
            .map((a) => ({
              id: a.id,
              title: a.title,
              bearbeiter: a.assigneeName,
              seit: a.updatedAt ? new Date(a.updatedAt) : null,
            })),
          /**
           * **Antworten auf Laufendes – und jede ausdrückliche Erwähnung** (Nils, 24.08.2026:
           * *„nur laufende … und bei denen wo ich explizit erwähnt wurde"*).
           *
           * Zwei Gründe, und beide sind eng: entweder es geht um etwas, das **gerade bei mir
           * läuft**, oder jemand hat mich **beim Namen genannt**. Ein `@` ist eine direkte
           * Ansprache – sie an einer Aufgabe zu übersehen, die nicht gerade offen ist, ist
           * genau der Fall, für den es die Zone gibt.
           *
           * Alles Übrige bleibt im Feed unter „Neu für dich". Weiter gefasst wäre diese Zone
           * an manchen Tagen der halbe Feed und damit wertlos.
           *
           * **Und was gerade abgehakt wurde** ([E-229](../../../../../docs/ENTSCHEIDUNGEN.md#e-229)):
           * die Zeile bleibt für die Dauer des Nachlaufs stehen, blass und mit gesetztem Haken,
           * damit ein Fehlgriff hier zurückzunehmen ist und nicht erst im Feed. Dieselbe Frist
           * wie dort – der Haken ist derselbe Haken (`feed_gelesen`).
           */
          antworten: feed
            .filter(
              (e) =>
                /**
                 * **Ein Kommentar zählt, wenn er zu einer laufenden Aufgabe gehört oder mich
                 * anspricht. Alles andere nur, wenn es mich anspricht** (18.09.2026,
                 * [E-366](../../../../../docs/ENTSCHEIDUNGEN.md#e-366)).
                 *
                 * Vorher stand hier nur `typ === 'comment'`. Damit fiel die Erwähnung in der
                 * Beschreibung durch: Die Mail ging raus, in „Klopft an" stand nichts – der
                 * unangenehmste Fall, weil man den Fehler erst bemerkt, wenn sich jemand
                 * wundert, warum er nichts gesehen hat.
                 *
                 * Die Bedingung bleibt eng: Ein Statuswechsel an einer fremden Aufgabe klopft
                 * weiterhin nicht an, denn er erwähnt niemanden.
                 */
                (e.typ === 'comment'
                  ? laufendeIds.has(e.taskId) || e.erwaehntMich
                  : e.erwaehntMich) &&
                (!e.gelesenAm ||
                  new Date(e.am) > new Date(e.gelesenAm) ||
                  imNachlauf(e.gelesenAm, jetztMs)),
            )
            .map((e) => ({
              taskId: e.taskId,
              titel: e.titel,
              wer: e.wer,
              am: new Date(e.am),
              gelesenAm: e.gelesenAm ? new Date(e.gelesenAm) : null,
              /**
               * **Der Unterschied, den die Zone bisher verschluckt hat** (28.08.2026, Z-11).
               * Beide Gründe standen hier oben schon nebeneinander – nur hieß das Ergebnis in
               * beiden Fällen „Antwort". Ab hier trennt `klopftAn` sie.
               */
              erwaehntMich: e.erwaehntMich,
            })),
          vorwarnungen,
        })
      : []

    /* ── Die Zahl an der Feed-Zeile (Z-11) ─────────────────────────────── */
    /**
     * **Dieselbe Frage wie im Feed, mit denselben Regeln beantwortet.**
     *
     * `gelesenAm` steht je Ereignis, nicht je Aufgabe (E-203) – wer eine Aufgabe abgehakt hat
     * und danach einen neuen Kommentar bekommt, ist wieder ungelesen. Der Nachlauf zählt
     * absichtlich **nicht** mit: eine eben abgehakte Zeile bleibt im Feed noch zehn Minuten
     * blass stehen, aber sie ist gelesen. Stünde sie in der Zahl, ginge die Zahl beim Abhaken
     * nicht herunter – und das ist genau der Moment, in dem man hinsieht.
     */
    const ungelesen = feed.filter((e) => !e.gelesenAm || new Date(e.am) > new Date(e.gelesenAm))
    const neuFuerDich = {
      aufgaben: new Set(ungelesen.map((e) => e.taskId)).size,
      erwaehnungen: new Set(ungelesen.filter((e) => e.erwaehntMich).map((e) => e.taskId)).size,
    }

    /* ── Auf dem Schirm (Z-03) ─────────────────────────────────────────── */
    /**
     * **Hier wird nichts entschieden, nur nachgeschlagen.** Ob ein Pin sichtbar ist, sagt
     * `pinzustand`; ob eine Wartezeile bleibt, sagt `wartetAufAntwort`. Beides steht geprüft
     * in `shared/schirm.ts` – diese Stelle sammelt die Beschriftungen dazu.
     */
    const pins = schirm?.pins ?? []
    const bereichLabel = new Map<string, string>()
    /**
     * **Die Bereiche stehen schon im Bestand** – `tasks.all` führt `scope` und `scopeLabel`
     * seit dem 03.08.2026 (F-32). Eine eigene Abfrage dafür wäre eine zweite Liste derselben
     * Sache; hier wird sie nur aus dem zusammengefaltet, was ohnehin geladen ist (Leitplanke 2).
     */
    const offeneJeBereich = new Map<string, number>()
    for (const a of aufgaben) {
      if (!a.scope) continue
      const s = `${a.sourceId}:${a.scope}`
      bereichLabel.set(s, a.scopeLabel ?? a.scope)
      if (isOpenStatus(a.status as TaskStatus)) {
        offeneJeBereich.set(s, (offeneJeBereich.get(s) ?? 0) + 1)
      }
    }

    const zuZeile = (p: (typeof pins)[number]): { zeile: Schirmzeile; zustand: string } => {
      const ziel = p.ziel
      const angelegtAm = new Date(p.angelegtAm)
      let titel = 'nicht mehr vorhanden'
      let auskunft: string | null = null
      let verwaist = false
      let erledigtAm: string | null = null

      if (ziel.art === 'aufgabe') {
        const a = jeId.get(ziel.taskId)
        if (a) {
          titel = a.title
          auskunft = a.assigneeName
          if (a.status === 'erledigt') erledigtAm = tagesschluessel(new Date(a.updatedAt))
        } else {
          verwaist = true
        }
      } else if (ziel.art === 'vorhaben') {
        const v = vorhaben.find((x) => x.id === ziel.vorhabenId)
        if (v) {
          titel = v.label
          const prozent =
            v.stand.gesamt > 0 ? Math.round((v.stand.erledigt / v.stand.gesamt) * 100) : 0
          auskunft = `${prozent} % · ${v.stand.offen} offen`
        } else {
          verwaist = true
        }
      } else {
        const s = `${ziel.sourceId}:${ziel.scope}`
        const label = bereichLabel.get(s)
        if (label) {
          titel = label
          auskunft = `${offeneJeBereich.get(s) ?? 0} offen`
        } else {
          /**
           * **Der vorhergesehene Fall.** Ein Bereich ist kein Datensatz, sondern das Paar
           * (`source_id`, `source_scope`) an der Aufgabe – fällt er aus der Konfiguration,
           * nimmt kein Fremdschlüssel den Pin mit. Die Zeile sagt das, statt leer dazustehen.
           */
          titel = ziel.scope
          verwaist = true
        }
      }

      return {
        zeile: {
          ziel,
          schluessel: pinzielSchluessel(ziel),
          titel,
          notiz: p.notiz,
          wiedervorlage: p.wiedervorlage,
          angelegtAm,
          auskunft,
          weckruf: p.wiedervorlage === heute,
          erledigt: erledigtAm !== null,
          verwaist,
        },
        zustand: pinzustand(p, erledigtAm, heute),
      }
    }

    const bewertet = pins.filter((p) => p.art === 'gepinnt').map(zuZeile)
    const angepinnt = pinReihenfolge(
      bewertet.filter((b) => b.zustand === 'sichtbar').map((b) => b.zeile),
      heute,
      (z) => z.erledigt,
    )
    const schlafendeZeilen = bewertet
      .filter((b) => b.zustand === 'schlaeft')
      .map((b) => b.zeile)
      .sort((a, b) => (a.wiedervorlage ?? '').localeCompare(b.wiedervorlage ?? ''))

    /** Die drei Wege, auf denen eine Aufgabe aus „Ich warte" verschwindet – siehe `schirm.ts`. */
    const zielAufgabe = (p: (typeof pins)[number]) =>
      p.ziel.art === 'aufgabe' ? p.ziel.taskId : null
    const warte = ichId
      ? wartetAufAntwort(
          (schirm?.wartekandidaten ?? []).map((w) => ({
            ...w,
            letzterKommentarAm: new Date(w.letzterKommentarAm),
          })),
          {
            ichId,
            jetzt: jetztZeit,
            gepinnt: new Set(
              bewertet
                .filter((b) => b.zustand === 'sichtbar' && b.zeile.ziel.art === 'aufgabe')
                .map((b) => (b.zeile.ziel.art === 'aufgabe' ? b.zeile.ziel.taskId : '')),
            ),
            stumm: new Set(
              pins.filter((p) => p.art === 'stumm').flatMap((p) => zielAufgabe(p) ?? []),
            ),
            schlafend: new Set(
              bewertet
                .filter((b) => b.zustand === 'schlaeft' && b.zeile.ziel.art === 'aufgabe')
                .map((b) => (b.zeile.ziel.art === 'aufgabe' ? b.zeile.ziel.taskId : '')),
            ),
          },
        )
      : []

    return {
      ichId,
      laedt: isLoading,
      klopft,
      angepinnt,
      warte,
      schlafend: {
        anzahl: schlafendeZeilen.length,
        naechste: naechsteWiedervorlage(pins, heute),
        zeilen: schlafendeZeilen,
      },
      markierungen: (schirm?.markierungen ?? []).map((m) => ({
        unteraufgabeId: m.unteraufgabeId,
        unteraufgabeTitel: m.unteraufgabeTitel,
        taskId: m.taskId,
        aufgabeTitel: m.aufgabeTitel,
      })),
      jetzt: inArbeit,
      wip,
      wipLimit,
      wipHinweisText: herkunft.limit === null ? null : wipHinweis(herkunft),
      wipBereich,
      fokusFuer,
      vorhabenInfo,
      spuren,
      ohneVorhaben,
      naechstes,
      genommen,
      zuvielHinweis: zuvielGenommen(genommen, wip, wipLimit, wipBereich),
      verlauf,
      heuteFertig,
      /**
       * **Eigenes Ziel schlägt den Schnitt** (Nils, 24.08.2026, [F-63](../../../../../docs/OFFENE-FRAGEN.md)).
       *
       * `null` heißt „aus dem Schnitt rechnen" und ist die Voreinstellung – nicht `3`. Wer nie
       * hingesehen hat, bekommt eine Zahl, die zu ihm passt; wer sich etwas vorgenommen hat,
       * bekommt seine eigene.
       */
      ziel: ich?.tagesziel ?? tagesziel(verlauf),
      eigenesZiel: ich?.tagesziel ?? null,
      serieTage: serie(verlauf, heute, frei),
      wartet,
      laengstesWarten,
      fuerMichImVorrat,
      vorratGesamt: ziehbar.length,
      bestandGesamt: aufgaben.length,
      neuFuerDich,
      pools,
      ziehbar,
    }
  }, [
    aufgaben,
    allePools,
    personen,
    vorhaben,
    abnahmen,
    feed,
    schirm,
    vorwarnung,
    meilensteine,
    abwesenheiten,
    tagesmarken,
    ichId,
    isLoading,
    jetztMs,
  ])
}
